import { Fragment } from "react";
import { VisualGroupHeading } from "../VisualGroupHeading";
import { groupHeading } from "../visualGroups";
import { ActionButton, ChoiceControl } from "../../components/ui";
import AutoCompleteDropdown from "../../components/ui/AutoCompleteDropdown";
import { optionImageStyle } from "../../components/ui/AutoCompleteDropdown/style";
import {
  ChoiceInstrument,
  DropdownInstrument,
} from "../../components/Wrappers/ChoiceInstrument";
import { useThemeContext } from "../../globalContext/global-context";
import type { QuestionViewProps } from "../contracts";
import type {
  MultiSelectDefinition,
  MultiSelectDraft,
  MultiSelectEvent,
} from "./core";
import { canToggleMultiSelectOption } from "./core";
import { SpecifyAnswerButtons } from "../specify/SpecifyAnswerButtons";

type MultiSelectViewProps = QuestionViewProps<
  MultiSelectDefinition,
  MultiSelectDraft,
  MultiSelectEvent
>;

const isOptionDisabled = (props: MultiSelectViewProps, id: number) =>
  !canToggleMultiSelectOption(props.definition, props.draft, id);

const Choices = (props: MultiSelectViewProps) => {
  const { arrangement } = props.display;
  const isList = props.display.view === "list";
  return (
    <ChoiceInstrument arrangement={arrangement} isList={isList}>
      {props.definition.options.map((option, index) => {
        const selected = props.draft.selectedIds.includes(option.id);
        const disabled = isOptionDisabled(props, option.id);
        const currentVal = {
          value: option.id,
          text: props.draft.customTexts[option.id] ?? option.label,
          disabled,
          display_text: option.displayText,
          image: option.image,
        };
        const common = {
          currentVal,
          adaptiveText: { maxLines: 3 },
          disabled,
          isSelected: selected,
          onClick: () =>
            option.customPrompt !== undefined
              ? props.controls.specify?.open({
                  kind: "option-answer",
                  optionId: option.id,
                })
              : props.dispatch({ type: "toggle", id: option.id }),
          testId: `chip-${index + 1}`,
        };
        const choice = isList ? (
          <ChoiceControl
            {...common}
            mode="checkbox"
            buttonImageStyle={optionImageStyle}
            controlAriaLabel={option.label}
            controlName="multi-select-choice"
            controlValue={String(option.id)}
            sx={{ textAlign: "start" }}
          />
        ) : (
          <ActionButton
            {...common}
            ariaLabel={option.label}
            ariaPressed={selected}
          />
        );
        return (
          <Fragment key={option.id}>
            <VisualGroupHeading
              text={groupHeading(props.definition.optionGroups, option.id)}
            />
            {choice}
          </Fragment>
        );
      })}
    </ChoiceInstrument>
  );
};

export const MultiSelectButtonsView = (props: MultiSelectViewProps) => (
  <Choices {...props} />
);
export const MultiSelectListView = (props: MultiSelectViewProps) => (
  <Choices {...props} />
);

export const MultiSelectDropdownView = (props: MultiSelectViewProps) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  const options = props.definition.options.map((option) => ({
    value: option.id,
    text: props.draft.customTexts[option.id] ?? option.label,
    disabled: isOptionDisabled(props, option.id),
    display_text: option.displayText,
    image: option.image,
  }));
  return (
    <DropdownInstrument>
      <AutoCompleteDropdown
        testId="multi-select-input"
        multiple
        options={options}
        groups={props.definition.optionGroups}
        value={options.filter((option) =>
          props.draft.selectedIds.includes(option.value),
        )}
        onSelect={(selection) => {
          const items = Array.isArray(selection) ? selection : [selection];
          const added = items.find(
            (item) => !props.draft.selectedIds.includes(item.value),
          );
          if (
            added &&
            props.definition.options.some(
              (option) =>
                option.id === added.value && option.customPrompt !== undefined,
            )
          ) {
            props.controls.specify?.open({
              kind: "option-answer",
              optionId: added.value,
            });
          } else {
            props.dispatch({
              type: "replace-selection",
              ids: items.map((item) => item.value),
            });
          }
        }}
        onClear={() => props.dispatch({ type: "replace-selection", ids: [] })}
        primaryColor={theme.tokens.color.action.primary}
        inputBgColor={theme.tokens.color.surface.card}
      />
      <SpecifyAnswerButtons control={props.controls.specify} />
    </DropdownInstrument>
  );
};
